/* eslint-disable @atlassian/relay/graphql-naming */
import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ui_softwareReleasesVersionRelatedWork_RelatedWork$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionRelatedWork_RelatedWork.graphql';
import type { ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery.graphql';
import { useAddRelatedWorkExperienceFail } from '@atlassian/jira-software-releases-add-related-work-to-version/src/services/experience-tracker/add-related-work/index.tsx';
import {
	RelatedWorkMutationLoadingProvider,
	useRelatedWorkMutationLoading,
} from '@atlassian/jira-software-releases-add-related-work-to-version/src/services/related-work-mutation-loading/index.tsx';
import { AddRelatedWorkPopup } from '@atlassian/jira-software-releases-add-related-work-to-version/src/ui/add-related-work-popup/async.tsx';
import {
	CollapsibleSection,
	CollapsibleToggleButton,
} from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { ErrorBoundary } from '../common/utils/error-boundary.tsx';

import RelatedWorkAnalyticsContext from './analytics-context/index.tsx';
import { CategoryFilter } from './category-filter/index.tsx';
import { RelatedWorkEmptyState } from './empty-state/index.tsx';
import messages from './messages.tsx';
import { RelatedWorkList } from './related-work-list/async.tsx';

export const RELATED_WORK_SHOW_MORE_THRESHOLD = 5;

type Props = {
	queryRef: PreloadedQuery<ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery>;
};

export const RelatedWork = ({ queryRef }: Props): React.JSX.Element => {
	return (
		<RelatedWorkMutationLoadingProvider>
			<RelatedWorkMain queryRef={queryRef} />
		</RelatedWorkMutationLoadingProvider>
	);
};

const RelatedWorkMain = ({ queryRef }: Props) => {
	const { formatMessage } = useIntl();
	const { isAddingRelatedWork } = useRelatedWorkMutationLoading();

	const { jira } = usePreloadedQuery<ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery(
				$versionAri: ID!
				$isDevopsFeatureDisabledInFedRamp: Boolean!
			) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						...ui_softwareReleasesVersionRelatedWork_RelatedWork
							@skip(if: $isDevopsFeatureDisabledInFedRamp)
					}
				}
			}
		`,
		queryRef,
	);
	const versionFrag: ui_softwareReleasesVersionRelatedWork_RelatedWork$key = jira.version;

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesVersionRelatedWork_RelatedWork on JiraVersion {
				relatedWorkV2(first: 100)
					@connection(key: "version_relatedWorkV2")
					@required(action: THROW) {
					edges @required(action: THROW) {
						__typename
						...relatedWorkList_softwareReleasesVersionRelatedWork_relatedWorkEdges
						...categoryFilter_softwareReleasesVersionRelatedWork
						...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_relatedWorkEdges
						...emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges
						...analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext
						node {
							...async_softwareReleasesAddRelatedWorkToVersion_EditRelatedWorkPopup_jiraVersionRelatedWork
							category
						}
					}
				}
				status
				...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_jiraVersion
				...async_softwareReleasesAddRelatedWorkToVersion_EditRelatedWorkPopup_jiraVersion
				...emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion
				...relatedWorkList_softwareReleasesVersionRelatedWork_jiraVersion
				...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
			}
		`,
		versionFrag,
	);

	const [selectedCategory, setSelectedCategory] = useQueryParam('relatedWorkCategory');
	const addRelatedWorkExpFailed = useAddRelatedWorkExperienceFail();

	const allRelatedWork = version.relatedWorkV2.edges.filter(Boolean);

	const relatedWorkInCategory =
		selectedCategory === undefined
			? allRelatedWork
			: allRelatedWork.filter(({ node }) => node?.category === selectedCategory);

	const body =
		allRelatedWork.length === 0 ? (
			<>
				{isAddingRelatedWork && fg('jira-releases-related-work-remove-optimistic-resp') ? (
					<Box xcss={addRelatedWorkLoadingStyles}>
						<Skeleton isShimmering borderRadius={2} width="100%" height="52px" />
					</Box>
				) : (
					<RelatedWorkEmptyState jiraVersion={version} relatedWorkEdges={allRelatedWork} />
				)}
			</>
		) : (
			<>
				<ErrorBoundary id="relatedWorkCategoryFilter" fallback="flag">
					<CategoryFilter
						relatedWorkEdges={allRelatedWork}
						selectedCategory={selectedCategory}
						onCategorySelected={(category) => setSelectedCategory(category, 'replace')}
					/>
				</ErrorBoundary>
				<RelatedWorkList
					jiraVersion={version}
					relatedWorkEdges={relatedWorkInCategory}
					selectedCategory={selectedCategory}
				/>
				{isAddingRelatedWork && fg('jira-releases-related-work-remove-optimistic-resp') && (
					<Box xcss={addRelatedWorkLoadingStyles}>
						<Skeleton isShimmering borderRadius={2} width="100%" height="52px" />
					</Box>
				)}
				<ActionsContainer>
					{version.status != null && version.status !== 'ARCHIVED' && (
						<ErrorBoundary
							id="addRelatedPopup"
							fallback="flag"
							onError={(location: string, error: Error) => {
								addRelatedWorkExpFailed(location, error);
							}}
						>
							<AddRelatedWorkPopup
								jiraVersion={version}
								relatedWorkEdges={allRelatedWork}
								renderTrigger={({ triggerProps, isLoading, onClick, onMouseOver }) => (
									<AddButtonContainer>
										<AddButton
											{...triggerProps}
											appearance="subtle-link"
											testId={
												isLoading
													? undefined
													: 'software-releases-version-related-work.ui.add-button'
											}
											iconBefore={
												<ActionIconContainer>
													<AddIcon label="" />
												</ActionIconContainer>
											}
											onClick={onClick}
											onMouseOver={onMouseOver}
											shouldFitContainer
										>
											<ShowMoreTextContainer>
												{formatMessage(messages.addRelatedWork)}
											</ShowMoreTextContainer>
										</AddButton>
									</AddButtonContainer>
								)}
							/>
						</ErrorBoundary>
					)}
				</ActionsContainer>
			</>
		);

	return (
		<RelatedWorkAnalyticsContext relatedWorkEdges={allRelatedWork}>
			<FireScreenAnalytics />
			<CollapsibleSection
				jiraVersion={version}
				uiType="RELATED_WORK"
				renderHeader={(isCollapsed, onToggleClicked) => (
					<HeaderWrapper>
						<HeaderLeftWrapper onClick={onToggleClicked}>
							<Heading size="small" as="h2">
								<Box xcss={sectionTitleStyles}>{formatMessage(messages.relatedWorkHeading)}</Box>
							</Heading>
							<TotalCountLozenge>{allRelatedWork.length}</TotalCountLozenge>
							<CollapsibleToggleButton isCollapsed={isCollapsed} />
						</HeaderLeftWrapper>
						<HeaderRightWrapper>
							{version.status !== 'ARCHIVED' && (
								<ErrorBoundary
									key="addRelatedWorkButton"
									id="addRelatedPopupHeaderPlusButton"
									fallback="flag"
									onError={(location: string, error: Error) => {
										addRelatedWorkExpFailed(location, error);
									}}
								>
									<AddRelatedWorkPopup
										jiraVersion={version}
										relatedWorkEdges={allRelatedWork}
										renderTrigger={({ triggerProps, onClick, onMouseOver }) => (
											<AddIconButtonContainer>
												<Button
													{...triggerProps}
													appearance="subtle"
													iconBefore={<AddIcon label="" />}
													onClick={onClick}
													onMouseOver={onMouseOver}
													shouldFitContainer
													aria-label={formatMessage(messages.addRelatedWork)}
												/>
											</AddIconButtonContainer>
										)}
									/>
								</ErrorBoundary>
							)}
						</HeaderRightWrapper>
					</HeaderWrapper>
				)}
				renderBody={() => body}
			/>
		</RelatedWorkAnalyticsContext>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.200'),
	paddingTop: token('space.100'),
	paddingLeft: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionIconContainer = styled.span({
	paddingRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShowMoreTextContainer = styled.span({
	fontWeight: token('font.weight.regular'),
});

// RLSS-2812 color.background.accent.gray.subtlest might be better than color.background.neutral

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddButtonContainer = styled.div({
	width: '100%',
	borderRadius: token('radius.medium'),
	cursor: 'pointer',
	paddingTop: token('space.100'),
	paddingRight: 0,
	paddingBottom: token('space.100'),
	paddingLeft: 0,
	'&:hover': {
		backgroundColor: `${token('color.background.neutral')}`,
	},
	'&:focus-within': {
		backgroundColor: `${token('color.background.neutral')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': { textAlign: 'left' },
});

const sectionTitleStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.300',
	marginRight: 'space.100',
});

const addRelatedWorkLoadingStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddIconButtonContainer = styled.div({
	width: token('space.400'),
	height: token('space.400'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginTop: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLeftWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRightWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TotalCountLozenge = styled.div({
	backgroundColor: token('color.background.accent.gray.subtlest'),

	font: token('font.body.small'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: token('space.200'),
	borderRadius: token('radius.large'),
	// This margin is to align vertically, due to font style, 2 pixels are misaligned that looks not right.
	marginTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingLeft: token('space.050'),
});
